import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container, Card, CardBody, CardHeader, Row, Col, Table, Button, Badge,
  Input, Label, Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap";
import axiosInstance from "../Request";
import { URL } from "../api";

const money = (v) =>
  v == null ? "—" : `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtWhen = (s) => (s ? String(s).replace("T", " ").slice(0, 16) : "—");

// High-contrast badge colors (white text) so every status is legible.
const MATCH_META = {
  MATCHED: { label: "Matched", bg: "#2dce89" },        // green
  AMT_MISMATCH: { label: "Amount Mismatch", bg: "#fb6340" }, // orange
  MISSING_SYS: { label: "Missing In System", bg: "#f5365c" }, // red
  MISSING_BANK: { label: "Missing In Bank", bg: "#5e72e4" },  // indigo
  REVERSED: { label: "Reversed", bg: "#32325d" },      // dark slate
  INFO: { label: "Info", bg: "#8898aa" },              // grey
};
const matchBadgeStyle = (bg) => ({ backgroundColor: bg || "#8898aa", color: "#fff" });
const typeBadgeStyle = (type) => ({ backgroundColor: type === "payin" ? "#11998e" : "#fb6340", color: "#fff" });
const RESO_COLORS = { Open: "secondary", Investigating: "warning", Resolved: "success", Ignored: "dark" };
const RESO_OPTIONS = ["Open", "Investigating", "Resolved", "Ignored"];
const ACTION_OPTIONS = ["Mark payout SUCCESS", "Mark payout FAILED", "Mark reversed", "Book in system", "Ignore"];

// Bank Reconciliation — upload payin/payout statements, review each run's matched vs. exception rows.
const LoanReco = () => {
  const [batches, setBatches] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selected, setSelected] = useState(null); // { batch, entries }
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [uploading, setUploading] = useState(false);
  const payinRef = useRef(null);
  const payoutRef = useRef(null);

  // Modals
  const [resoModal, setResoModal] = useState(null); // line
  const [resoStatus, setResoStatus] = useState("Open");
  const [resoNote, setResoNote] = useState("");
  const [actionModal, setActionModal] = useState(null); // line
  const [actionType, setActionType] = useState(ACTION_OPTIONS[0]);
  const [actionNote, setActionNote] = useState("");

  const fetchBatches = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await axiosInstance.get(URL.RECO_BATCHES);
      setBatches(Array.isArray(res.data) ? res.data : []);
    } catch { setBatches([]); } finally { setLoadingList(false); }
  }, []);

  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  const openBatch = async (id) => {
    setLoadingDetail(true);
    try {
      const res = await axiosInstance.get(`${URL.RECO_BATCH}/${id}`);
      setSelected(res.data);
    } catch { setSelected(null); } finally { setLoadingDetail(false); }
  };

  const doUpload = async (type, file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", type);
      // Same proven multipart pattern the app uses elsewhere (UserManagement upload).
      await axiosInstance.post(URL.RECO_UPLOAD, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      await fetchBatches();
    } catch (e) {
      alert(e.response?.data?.error || e.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (payinRef.current) payinRef.current.value = "";
      if (payoutRef.current) payoutRef.current.value = "";
    }
  };

  const saveResolution = async () => {
    try {
      await axiosInstance.post(`${URL.RECO_LINE}/${resoModal.id}/resolution`,
        { resolution: resoStatus, note: resoNote }, { withCredentials: true });
      setResoModal(null);
      if (selected) openBatch(selected.batch.id);
    } catch (e) { alert(e.response?.data?.error || e.message || "Could not update resolution."); }
  };

  const applyAction = async () => {
    try {
      await axiosInstance.post(`${URL.RECO_LINE}/${actionModal.id}/action`,
        { action: actionType, note: actionNote, resolution: "Resolved" }, { withCredentials: true });
      setActionModal(null);
      if (selected) openBatch(selected.batch.id);
    } catch (e) { alert(e.response?.data?.error || e.message || "Could not apply action."); }
  };

  const openReso = (line) => { setResoModal(line); setResoStatus(line.resolution || "Open"); setResoNote(line.resolutionNote || ""); };
  const openAction = (line) => { setActionModal(line); setActionType(ACTION_OPTIONS[0]); setActionNote(""); };

  // ---------- RUN DETAIL ----------
  if (selected) {
    const b = selected.batch;
    const entries = selected.entries || [];
    return (
      <Container className="mt--7" fluid>
        <Card className="bg-gradient-default shadow mb-4">
          <CardBody>
            <h6 className="text-uppercase text-light ls-1 mb-1">Salora Capital · NBFC</h6>
            <h2 className="text-white mb-0">Reconciliation — Loan</h2>
          </CardBody>
        </Card>

        <Button color="secondary" size="sm" outline className="mb-3" onClick={() => setSelected(null)}>
          <i className="fa fa-chevron-left mr-1" /> All runs
        </Button>

        <Card className="shadow mb-4">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
              <div>
                <Badge className="mr-2 text-capitalize" style={typeBadgeStyle(b.recoType)}>{b.recoType}</Badge>
                <span className="font-weight-bold">{b.fileName}</span>{" "}
                <Badge color="success" pill className="ml-2">{b.status}</Badge>
              </div>
              <div className="text-muted small">{fmtWhen(b.createdAt)}{b.uploadedBy ? ` · ${b.uploadedBy}` : ""}</div>
            </div>
            <Row>
              {[
                ["Rows", b.totalRows, "#32325d"],
                ["Matched", b.matched, "#2dce89"],
                ["Amt Mismatch", b.amtMismatch, "#fb6340"],
                ["Missing (Sys)", b.missingSys, "#f5365c"],
                ["Missing (Bank)", b.missingBank, "#11cdef"],
                ["Reversed", b.reversed, "#8898aa"],
                ["Info", b.info, "#8898aa"],
              ].map(([label, val, color]) => (
                <Col key={label} xs="4" md="2" className="mb-2">
                  <div className="text-uppercase text-muted small font-weight-bold">{label}</div>
                  <div className="h2 mb-0 font-weight-bold" style={{ color }}>{val ?? 0}</div>
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>

        <Card className="shadow">
          <CardHeader className="border-0">
            <h3 className="mb-0">Entries <small className="text-muted">{entries.length} record(s)</small></h3>
          </CardHeader>
          {loadingDetail ? (
            <CardBody className="text-center text-muted py-5">Loading…</CardBody>
          ) : (
            <div className="table-scroll">
              <Table className="align-items-center table-flush">
                <thead className="thead-light">
                  <tr>
                    <th>Reference</th><th>Loan ID</th><th>Match</th><th>File Amt</th><th>System Amt</th>
                    {b.recoType === "payin" && (<><th>Principal</th><th>Interest</th><th>Fee</th></>)}
                    <th>RRN</th><th>Txn Date</th><th>Resolution</th><th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr><td colSpan={b.recoType === "payin" ? 12 : 9} className="text-center text-muted py-3">No entries</td></tr>
                  ) : entries.map((e) => {
                    const m = MATCH_META[e.matchStatus] || { label: e.matchStatus, bg: "#8898aa" };
                    return (
                      <tr key={e.id}>
                        <td style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{e.reference || e.utr || "—"}</td>
                        <td>{e.loanId || "—"}</td>
                        <td><Badge pill style={matchBadgeStyle(m.bg)}>{m.label}</Badge></td>
                        <td>{money(e.bankAmount)}</td>
                        <td>{money(e.sysAmount)}</td>
                        {b.recoType === "payin" && (<>
                          <td>{e.principal == null ? "—" : money(e.principal)}</td>
                          <td>{e.interest == null ? "—" : money(e.interest)}</td>
                          <td>{e.fee == null ? "—" : money(e.fee)}</td>
                        </>)}
                        <td>{e.rrn || "—"}</td>
                        <td>{e.txnDate || "—"}</td>
                        <td><Badge color={RESO_COLORS[e.resolution] || "secondary"} pill>{e.resolution || "Open"}</Badge></td>
                        <td>
                          <Button color="primary" size="sm" className="mr-1" onClick={() => openAction(e)}>Action</Button>
                          <Button color="secondary" size="sm" outline onClick={() => openReso(e)}>Resolve</Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Card>

        {/* Resolution modal */}
        <Modal isOpen={!!resoModal} toggle={() => setResoModal(null)} centered>
          <ModalHeader toggle={() => setResoModal(null)}>Update resolution</ModalHeader>
          <ModalBody>
            <Label className="small font-weight-bold">Resolution status</Label>
            <Input type="select" value={resoStatus} onChange={(e) => setResoStatus(e.target.value)}>
              {RESO_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </Input>
            <Label className="small font-weight-bold mt-3">Note</Label>
            <Input type="textarea" value={resoNote} placeholder="e.g. Checking with bank ops"
              onChange={(e) => setResoNote(e.target.value)} />
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setResoModal(null)}>Cancel</Button>
            <Button color="primary" onClick={saveResolution}>Save</Button>
          </ModalFooter>
        </Modal>

        {/* Corrective action modal */}
        <Modal isOpen={!!actionModal} toggle={() => setActionModal(null)} centered>
          <ModalHeader toggle={() => setActionModal(null)}>Apply corrective action</ModalHeader>
          <ModalBody>
            {actionModal && (
              <div className="bg-secondary rounded p-3 mb-3 small">
                <div className="d-flex justify-content-between"><span className="text-muted">Reference</span><span>{actionModal.reference || actionModal.utr || "—"}</span></div>
                <div className="d-flex justify-content-between"><span className="text-muted">Match status</span>
                  <span>{(MATCH_META[actionModal.matchStatus] || {}).label || actionModal.matchStatus}</span></div>
              </div>
            )}
            <Label className="small font-weight-bold">Action</Label>
            <Input type="select" value={actionType} onChange={(e) => setActionType(e.target.value)}>
              {ACTION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </Input>
            <Label className="small font-weight-bold mt-3">Note</Label>
            <Input type="textarea" value={actionNote} placeholder="Recorded on the entry (e.g. Bank reversed the payout)"
              onChange={(e) => setActionNote(e.target.value)} />
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" outline onClick={() => setActionModal(null)}>Cancel</Button>
            <Button color="primary" onClick={applyAction}>Apply action</Button>
          </ModalFooter>
        </Modal>
      </Container>
    );
  }

  // ---------- BATCH LIST ----------
  return (
    <Container className="mt--7" fluid>
      <Card className="bg-gradient-default shadow mb-4">
        <CardBody>
          <h6 className="text-uppercase text-light ls-1 mb-1">Salora Capital · NBFC</h6>
          <h2 className="text-white mb-0">Reconciliation — Loan</h2>
        </CardBody>
      </Card>

      <Card className="shadow">
        <CardHeader className="border-0 d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h3 className="mb-0">Bank Reconciliation</h3>
            <small className="text-muted">Upload settlement / VA statements and review matched vs. exception rows</small>
          </div>
          <div>
            <input ref={payinRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }}
              onChange={(e) => doUpload("payin", e.target.files[0])} />
            <input ref={payoutRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }}
              onChange={(e) => doUpload("payout", e.target.files[0])} />
            <Button color="secondary" outline size="sm" className="mr-2" disabled={uploading}
              onClick={() => payinRef.current && payinRef.current.click()}>
              <i className="fa fa-arrow-down mr-1" /> Upload Payin
            </Button>
            <Button color="primary" size="sm" disabled={uploading}
              onClick={() => payoutRef.current && payoutRef.current.click()}>
              <i className="fa fa-arrow-up mr-1" /> Upload Payout
            </Button>
          </div>
        </CardHeader>
        {loadingList ? (
          <CardBody className="text-center text-muted py-5">Loading runs…</CardBody>
        ) : (
          <div className="table-scroll">
            <Table className="align-items-center table-flush">
              <thead className="thead-light">
                <tr>
                  <th>When</th><th>Type</th><th>File</th><th>Status</th>
                  <th>Rows</th><th>Matched</th><th>Amt Mismatch</th><th>Missing (Sys)</th><th>Missing (Bank)</th>
                </tr>
              </thead>
              <tbody>
                {batches.length === 0 ? (
                  <tr><td colSpan="9" className="text-center text-muted py-4">No runs yet — upload a Payin or Payout statement.</td></tr>
                ) : batches.map((b) => (
                  <tr key={b.id} style={{ cursor: "pointer" }} onClick={() => openBatch(b.id)}>
                    <td>{fmtWhen(b.createdAt)}</td>
                    <td><Badge className="text-capitalize" style={typeBadgeStyle(b.recoType)}>{b.recoType}</Badge></td>
                    <td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>{b.fileName}</td>
                    <td><Badge color="success" pill>{b.status}</Badge></td>
                    <td>{(b.totalRows ?? 0).toLocaleString("en-IN")}</td>
                    <td className="font-weight-bold text-success">{b.matched ?? 0}</td>
                    <td className={b.amtMismatch ? "text-warning font-weight-bold" : ""}>{b.amtMismatch ?? 0}</td>
                    <td className={b.missingSys ? "text-danger font-weight-bold" : ""}>{b.missingSys ?? 0}</td>
                    <td className={b.missingBank ? "text-danger font-weight-bold" : ""}>{b.missingBank ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </Container>
  );
};

export default LoanReco;
